/**
 * Cookie Utilities
 *
 * Helper functions for reading and writing cookies.
 * Used for cross-application data sharing (e.g., theme preferences with Drupal CMS).
 */

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }

  return null;
}

/**
 * Set a cookie with optional expiration
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Expiration in days (default: 365)
 * @param path - Cookie path (default: '/')
 * @param sameSite - SameSite attribute (default: 'Lax')
 */
export function setCookie(
  name: string,
  value: string,
  days: number = 365,
  path: string = '/',
  sameSite: 'Strict' | 'Lax' | 'None' = 'Lax',
  domain?: string
): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  let cookieString = `${name}=${encodeURIComponent(value)}`;
  cookieString += `; expires=${expires.toUTCString()}`;
  cookieString += `; path=${path}`;
  cookieString += `; SameSite=${sameSite}`;

  // Set domain for cross-subdomain sharing if provided.
  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  // Add Secure flag if using HTTPS
  if (window.location.protocol === 'https:') {
    cookieString += '; Secure';
  }

  document.cookie = cookieString;
}

/**
 * Get the root domain for cross-subdomain cookie sharing within an env.
 * 3-part hostnames (e.g. prod "em3.intellifirenwt.com") share at the
 * registrable domain ".intellifirenwt.com". 4+ part hostnames (env-scoped
 * staging/UAT like "em3.staging.intellifirenwt.com") share at the env
 * parent ".staging.intellifirenwt.com" so cookies don't bleed cross-env.
 * Returns undefined for localhost (no domain needed).
 */
export function getRootDomain(): string | undefined {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return undefined;
  }
  const parts = hostname.split('.');
  if (parts.length >= 4) {
    return '.' + parts.slice(-3).join('.');
  }
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.');
  }
  return undefined;
}

/**
 * Delete a cookie by name
 * @param name - Cookie name
 * @param path - Cookie path (default: '/')
 */
export function deleteCookie(name: string, path: string = '/'): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
}

/**
 * Check if a cookie exists
 * @param name - Cookie name
 * @returns True if cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}
